import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Leaf, ArrowRight, ShieldCheck, Award, Zap,
  CheckCircle2, Phone, Mail, MapPin, Clock, ChevronRight, Star,
  Sprout, Shovel, Fence, Flower2
} from 'lucide-react';
import img1 from '../../assets/images/jpg/img1.jpg';
import img2 from '../../assets/images/jpg/img2.jpg';
import img3 from '../../assets/images/jpg/img3.jpg';
import img4 from '../../assets/images/jpg/img4.jpg';
import gallery1 from '../../assets/images/jpg/gallery1.jpg';
import gallery3 from '../../assets/images/jpg/gallery3.jpg';
import gallery4 from '../../assets/images/jpg/gallery4.jpg';
import gallery5 from '../../assets/images/jpg/gallery5.jpg';
import waterblasting from '../../assets/images/jpg/waterblasting.jpeg';
import lawnmaintance from '../../assets/images/jpg/lawnmaintance.jpeg';
import HedgeTrimming from '../../assets/images/jpg/HedgeTrimming.jpeg';
import hydroseeding from '../../assets/images/jpg/hydroseeding.jpg';
import newProject from '../../assets/images/jpg/new.jpeg';
import whyHireUs from '../../assets/images/jpg/why-hire-us.jpg';
import projectFence from '../../assets/images/jpg/project-fence.jpg';
import projectBackyard from '../../assets/images/jpg/project-backyard.jpg';

export default function App() {
  const heroVariants = {
    container: { hidden: {}, show: { transition: { staggerChildren: 0.12 } } },
    fadeUp: { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } } },
    float: { animate: { y: [0, -10, 0], transition: { duration: 6, repeat: Infinity, ease: 'easeInOut' } } }
  };

  const cardVariant = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [formStatus, setFormStatus] = useState({ sending: false, message: '', error: false });
  const slidesToShow = 3;
  const gallerySlides = [
    gallery1,
    newProject,
    gallery3,
    gallery4,
    gallery5,
    projectFence,
    projectBackyard,
    waterblasting,
    lawnmaintance,
    HedgeTrimming

  ];

  const testimonials = [
    { name: 'Eleanor Pena', role: 'Executive Director', text: 'Leafix transformed our front yard into the oasis we always wanted — exceptional service and great design.', img: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=150&q=80' },
    { name: 'Calvin Hawkins', role: 'Homeowner', text: 'They delivered a beautiful landscape upgrade, and the follow-up was instant and very professional.', img: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=150&q=80' }
  ];

  const businessEmail = 'Info@thelandscapingchristchurch.co.nz';
  const businessAddress = 'Christchurch, New Zealand';
  const businessMapUrl = 'https://maps.app.goo.gl/jpcSNMC3kdKK1b99A?g_st=iw';
  const businessMapEmbedUrl = 'https://www.google.com/maps?q=Christchurch%2C%20New%20Zealand&output=embed';
  const prevGallery = () => setGalleryIndex((current) => Math.max(current - 1, 0));
  const nextGallery = () => setGalleryIndex((current) => Math.min(current + 1, gallerySlides.length - slidesToShow));
  const handleEnquirySubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    setFormStatus({ sending: true, message: '', error: false });

    try {
      const response = await fetch('/contact.php', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || 'Unable to send your enquiry.');
      form.reset();
      setFormStatus({ sending: false, message: result.message, error: false });
    } catch (error) {
      setFormStatus({ sending: false, message: error.message || 'Unable to send your enquiry. Please try again.', error: true });
    }
  };

  return (
    <div className="bg-white text-slate-800 font-sans antialiased overflow-x-hidden selection:bg-lime-500 selection:text-emerald-950">

      {/* 1. HERO SECTION (Leafix Premium Style) */}
      <motion.section
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.25 }}
        variants={heroVariants.container}
        className="relative bg-emerald-950 text-white pt-32 pb-32 px-4 overflow-hidden"
      >
        {/* Subtle Background Pattern Layer */}
        <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1558904541-efa8c3a30fc9?auto=format&fit=crop&w=1200&q=80')] bg-cover bg-center"></div>
        
        <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Hero Left */}
          <motion.div variants={heroVariants.fadeUp} className="lg:col-span-7 space-y-6 text-left">
            <div className="inline-flex items-center gap-2 bg-lime-500/10 border border-lime-500/30 text-lime-400 text-xs font-bold px-4 py-2 rounded-full uppercase tracking-widest">
              <Leaf size={14} className="fill-current" /> Premium Gardening & Landscaping
            </div>
            <motion.h1 variants={heroVariants.fadeUp} className="text-4xl md:text-6xl font-black text-white tracking-tight leading-[1.1]">
              Natural & Beautiful <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-lime-400 to-emerald-400">
                Landscape Design
              </span>
            </motion.h1>
            <p className="text-base md:text-lg text-emerald-100/80 max-w-xl font-normal leading-relaxed">
              Bring nature closer to your life. We combine professional botanical architecture with creative lawn care to deliver premium residential and commercial spaces.
            </p>
            <motion.div variants={heroVariants.fadeUp} className="flex flex-wrap gap-4 pt-2">
              <motion.a whileTap={{ scale: 0.98 }} href="tel:+64228980029" className="bg-lime-500 hover:bg-lime-600 text-emerald-950 px-8 py-4 rounded-xl font-extrabold text-base transition-all shadow-lg shadow-lime-500/20 flex items-center gap-2 inline-flex">
                Enquiry Now <ArrowRight size={18} />
              </motion.a>
              <a href="#portfolio" className="bg-emerald-900/50 hover:bg-emerald-900 border border-emerald-800 text-white px-8 py-4 rounded-xl font-bold text-base transition-all inline-flex items-center justify-center">
                Our Showcase
              </a>
            </motion.div>
          </motion.div>

          {/* Hero Right (Leafix Featured Frame) */}
          <motion.div variants={heroVariants.fadeUp} className="lg:col-span-5 relative mt-8 lg:mt-0">
            <motion.div variants={cardVariant} className="relative mx-auto w-full max-w-[400px] h-[480px] bg-emerald-900/30 rounded-[2.5rem] border-4 border-emerald-800 p-4 shadow-2xl">
              <motion.img 
                src={img1} 
                alt="Leafix Premium Hero" 
                className="w-full h-full object-cover rounded-[2rem]"
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.6 }}
              />
              {/* Floating Info Card */}
              <motion.div variants={heroVariants.float} className="absolute -bottom-6 -left-6 bg-white text-emerald-950 p-5 rounded-2xl shadow-xl border border-slate-100 flex items-center gap-4 max-w-[240px]">
                <div className="bg-lime-100 text-lime-700 p-3 rounded-xl"><Award size={24} /></div>
                <div>
                  <p className="text-2xl font-black leading-none">500+</p>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mt-1"> projects</p>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* 2. SERVICES GRID SECTION (Leafix Clean Luxury Style) */}
      <motion.section initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={heroVariants.container} className="bg-stone-50 py-24 px-4 relative -mt-12 rounded-t-[3rem] z-10">
        <div className="max-w-7xl mx-auto">
          {/* Header text */}
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-3" id="services">
            <span className="text-lime-600 font-extrabold tracking-widest text-xs uppercase block">What We Do</span>
            <h2 className="text-3xl md:text-5xl font-black text-emerald-950 tracking-tight">
              Professional Services
            </h2>
            <div className="h-1.5 w-16 bg-lime-500 mx-auto rounded-full"></div>
          </div>

          {/* 4 Cards Grid */}
          <motion.div className="flex flex-wrap justify-center gap-8" variants={heroVariants.container}>
            {[
              { title: 'Landscaping Design & Build', icon: <Leaf />, img: img1, href: '/landscaping-design-build' },
              { title: 'Hydroseeding', icon: <Zap />, img: hydroseeding, href: '/hydroseeding' },
            
              { title: 'Washing & Water Blasting', icon: <Sprout />, img: waterblasting, href: '/washing-water-blasting' },
              { title: 'Property Maintenance', icon: <Flower2 />, img: gallery5, href: '/property-maintenance' },
              { title: 'Lawn Maintenance', icon: <CheckCircle2 />, img: lawnmaintance, href: '/lawn-maintenance' },
              { title: 'Fencing', icon: <ShieldCheck />, img: img4, href: '/fencing' }
            ].map((srv, idx) => (
              <motion.a key={idx} href={srv.href} variants={cardVariant} className="w-full sm:w-[48%] lg:w-[23%] bg-white rounded-3xl overflow-hidden shadow-sm border border-slate-100 hover:shadow-xl hover:border-lime-200 transition-all duration-200 p-5 flex flex-col justify-between group">
                <div>
                  <div className="h-44 rounded-2xl overflow-hidden mb-6 relative">
                    <img src={srv.img} alt={srv.title} className="w-full h-full object-cover" />
                  </div>
                  <h3 className="font-black text-emerald-950 text-xl mb-2">{srv.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    High-quality execution tailored completely around your botanical structure.
                  </p>
                </div>
                <div className="pt-6 mt-6 border-t border-slate-50 flex items-center justify-between text-sm">
                  <span className="font-extrabold text-lime-600 flex items-center gap-1 cursor-pointer">
                    Read Details <ChevronRight size={14} />
                  </span>
                  <div className="bg-emerald-50 text-emerald-800 p-2 rounded-xl">{srv.icon}</div>
                </div>
              </motion.a>
            ))}
          </motion.div>
        </div>
      </motion.section>


      {/* 3. EXPERIENCE SPLIT SHOWCASE (Image Frame + Checklist) */}
      <motion.section initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={heroVariants.container} className="max-w-7xl mx-auto px-4 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        {/* Left Side: Modern Asymmetric Image Frame */}
        <motion.div variants={cardVariant} className="relative">
          <div className="w-full max-w-[460px] h-[500px] bg-slate-100 rounded-[3rem] overflow-hidden shadow-xl border-8 border-white ring-1 ring-slate-100">
            <motion.img src={whyHireUs} alt="Landscaped entrance with hedges" className="w-full h-full object-cover" whileHover={{ scale: 1.02 }} transition={{ duration: 0.6 }} />
          </div>
          {/* Overlay Box */}
          <motion.div variants={heroVariants.fadeUp} className="absolute top-12 -right-4 bg-lime-500 text-emerald-950 p-6 rounded-3xl shadow-xl max-w-[200px] text-center border-4 border-white">
            <p className="text-4xl font-black">5+</p>
            <p className="text-xs font-black uppercase tracking-wider mt-1 text-emerald-900/80">Years of Quality</p>
          </motion.div>
        </motion.div>

        {/* Right Side: Copy & List */}
        <motion.div variants={heroVariants.fadeUp} className="space-y-6 text-left">
          <span className="text-lime-600 font-extrabold uppercase tracking-wider text-xs block">Why Hire Us</span>
          <h2 className="text-3xl md:text-5xl font-black text-emerald-950 tracking-tight leading-tight">
            We Maintain & Transform Outdoors Perfectly
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Leafix provides complete eco-friendly treatments. Our team arrives equipped with premium modern tools to promise 100% spotless execution.
          </p>
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2" variants={heroVariants.container}>
            {[
              'Licensed & Fully Insured',
              'Eco-friendly Fertilizer',
              'Commercial Grade Tools',
              'Free Layout Estimation',
              'Expert Horticulturists',
              '24/7 Priority Support'
            ].map((text, i) => (
              <motion.div key={i} variants={cardVariant} className="flex items-center gap-3 text-slate-800 font-bold text-sm">
                <CheckCircle2 className="text-lime-500 flex-shrink-0" size={18} />
                <span>{text}</span>
              </motion.div>
            ))}
          </motion.div>
          <div className="pt-4">
            <button className="bg-emerald-950 hover:bg-emerald-900 text-white font-extrabold px-8 py-4 rounded-xl transition shadow-lg shadow-emerald-950/20">
              More About Us
            </button>
          </div>
        </motion.div>
      </motion.section>


      {/* 5. PORTFOLIO GALLERY */}
      <motion.section id="portfolio" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={heroVariants.container} className="max-w-7xl mx-auto px-4 py-24">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
          <div className="text-left">
            <span className="text-lime-600 font-extrabold uppercase tracking-wider text-xs block">Recent Work</span>
            <h2 className="text-3xl md:text-5xl font-black text-emerald-950 tracking-tight mt-1">Our Beautiful Projects</h2>
          </div>
          <div className="flex gap-3">
            <button onClick={prevGallery} disabled={galleryIndex === 0} className="bg-white text-emerald-950 border border-slate-200 px-4 py-3 rounded-xl shadow-sm hover:bg-slate-100 transition disabled:opacity-40 disabled:cursor-not-allowed">Prev</button>
            <button onClick={nextGallery} disabled={galleryIndex >= gallerySlides.length - slidesToShow} className="bg-lime-500 text-emerald-950 px-4 py-3 rounded-xl shadow-sm hover:bg-lime-600 transition disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
        <div className="relative overflow-hidden rounded-[2rem] border border-slate-100 shadow-xl">
          <motion.div animate={{ x: `-${galleryIndex * (100 / slidesToShow)}%` }} transition={{ type: 'spring', stiffness: 120, damping: 20 }} className="flex w-full">
            {gallerySlides.map((img, index) => (
              <div key={index} className="min-w-[calc(100%/3)] pr-4">
                <motion.div variants={cardVariant} className="h-[320px] overflow-hidden rounded-[2rem] shadow-xl bg-slate-100">
                  <img src={img} alt={`Portfolio ${index + 1}`} className="w-full h-full object-cover" />
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.section>


      {/* 6. HOW WE BRING YOUR GARDEN TO LIFE */}
      <motion.section initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={heroVariants.container} className="bg-white py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto mb-20">
            <span className="text-emerald-800 font-extrabold uppercase tracking-wider text-base block">How It Work</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-950 tracking-tight mt-4">How We Bring Your Garden to Life</h2>
          </div>
          <motion.div variants={cardVariant} className="border border-slate-300 rounded-3xl overflow-hidden bg-white">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-y-10 px-8 md:px-10 pt-16 pb-12">
              {[
                { step: '01', title: 'Share Your Vision', text: 'Tell us about your outdoor space, ideas, and goals. We listen carefully.', icon: Sprout },
                { step: '02', title: 'Get a Custom Plan', text: 'Our team designs a personalized landscaping plan tailored to your needs.', icon: Shovel },
                { step: '03', title: 'On-Site Execution', text: 'We bring the design to life using quality plants, materials, and expert crews.', icon: Fence },
                { step: '04', title: 'Final Touch & Review', text: 'We refine every detail and ensure the result meets our quality standards.', icon: Flower2 }
              ].map((item, index) => {
                const Icon = item.icon;

                return (
                  <motion.div key={index} variants={cardVariant} className="text-center px-4">
                    <Icon className="mx-auto text-emerald-800 mb-6" size={68} strokeWidth={1.7} />
                    <h3 className="text-xl font-black text-slate-950">{item.title}</h3>
                  </motion.div>
                );
              })}
            </div>
            <div className="relative border-t border-slate-300">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-y-10 px-8 md:px-10 pt-0 pb-12">
                {[
                  { step: '01', text: 'Tell us about your outdoor space, ideas, and goals. We listen carefully.' },
                  { step: '02', text: 'Our team designs a personalized landscaping plan tailored to your needs.' },
                  { step: '03', text: 'We bring the design to life using quality plants, materials, and expert crews.' },
                  { step: '04', text: 'We refine every detail and ensure the result meets our quality standards.' }
                ].map((item, index) => (
                  <motion.div key={index} variants={cardVariant} className="relative px-4 pt-20 text-center">
                    <div className="absolute left-1/2 top-0 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-stone-100 text-slate-950 text-xl font-black">
                      {item.step}
                    </div>
                    <p className="text-slate-600 text-lg leading-8 max-w-[280px] mx-auto">{item.text}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>


      {/* 7. CUSTOMER TESTIMONIAL MODAL CARD */}
      <motion.section initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={heroVariants.container} className="max-w-6xl mx-auto px-4 py-24">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-lime-600 font-extrabold uppercase tracking-wider text-xs block">Client Testimonials</span>
          <h2 className="text-3xl md:text-5xl font-black text-emerald-950 tracking-tight mt-3">What Our Clients Say</h2>
          <p className="text-slate-600 mt-4 max-w-2xl mx-auto leading-relaxed">
            Real feedback from homeowners and businesses who trusted Leafix to transform their outdoor spaces with care and precision.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {testimonials.map((review, idx) => (
            <motion.div key={idx} variants={cardVariant} className="bg-white border-2 border-slate-100 rounded-[2.5rem] p-8 md:p-10 shadow-xl text-left">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-slate-100">
                  <img src={review.img} alt={review.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <h4 className="font-black text-emerald-950 text-lg">{review.name}</h4>
                  <p className="text-xs font-extrabold text-lime-600 uppercase tracking-wider">{review.role}</p>
                </div>
              </div>
              <div className="flex text-amber-400 gap-1 mb-4">
                <Star className="fill-current" size={18} /><Star className="fill-current" size={18} /><Star className="fill-current" size={18} /><Star className="fill-current" size={18} /><Star className="fill-current" size={18} />
              </div>
              <p className="text-slate-600 italic text-base md:text-lg font-medium leading-relaxed">{review.text}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>


      {/* 8. SPLIT INFO CONTACT & EXPERT CONSULTATION FORM */}
      <motion.section initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={heroVariants.container} className="bg-emerald-950 text-white py-24 px-4 relative" id='contact'>
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center" >
          {/* Info Column */}
          <motion.div variants={heroVariants.fadeUp} className="lg:col-span-5 space-y-6 text-left">
            <span className="text-lime-400 font-extrabold uppercase tracking-wider text-xs block">Contact Us</span>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight">
              Have Questions? Get In Touch
            </h2>
            <div className="space-y-6 pt-4">
              <div className="flex items-center gap-4">
                <div className="bg-white/5 p-3.5 rounded-xl border border-white/10 text-lime-400"><Phone size={20} /></div>
                <div><p className="text-xs text-emerald-200/60 font-bold uppercase tracking-wider">Call Anytime</p><p className="font-extrabold text-lg mt-0.5">+64 228 980 029</p></div>
              </div>
              <div className="flex items-center gap-4">
                <div className="bg-white/5 p-3.5 rounded-xl border border-white/10 text-lime-400"><Mail size={20} /></div>
                <div><p className="text-xs text-emerald-200/60 font-bold uppercase tracking-wider">Send Email</p><p className="font-extrabold text-lg mt-0.5">{businessEmail}</p></div>
              </div>
              <div className="flex items-center gap-4">
                <div className="bg-white/5 p-3.5 rounded-xl border border-white/10 text-lime-400"><Clock size={20} /></div>
                <div><p className="text-xs text-emerald-200/60 font-bold uppercase tracking-wider">Office Hours</p><p className="font-extrabold text-lg mt-0.5">Mon - Sat: 9am - 6pm</p></div>
              </div>
            </div>
          </motion.div>

          {/* Form Box Column */}
          <motion.form onSubmit={handleEnquirySubmit} variants={cardVariant} className="lg:col-span-7 bg-white text-slate-800 p-8 md:p-12 rounded-[2.5rem] shadow-2xl space-y-4">
            <h3 className="text-2xl font-black text-emerald-950 mb-2">Book Free Consultation</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input type="text" name="name" required placeholder="Your Name" className="p-4 bg-stone-50 border border-slate-200 rounded-xl font-medium text-sm focus:outline-none focus:border-lime-500" />
              <input type="email" name="email" required placeholder="Email Address" className="p-4 bg-stone-50 border border-slate-200 rounded-xl font-medium text-sm focus:outline-none focus:border-lime-500" />
            </div>
            <input type="tel" name="phone" placeholder="Phone Number" className="w-full p-4 bg-stone-50 border border-slate-200 rounded-xl font-medium text-sm focus:outline-none focus:border-lime-500" />
            <textarea name="message" required placeholder="Write message details..." rows={3} className="w-full p-4 bg-stone-50 border border-slate-200 rounded-xl font-medium text-sm focus:outline-none focus:border-lime-500"></textarea>
            <button type="submit" disabled={formStatus.sending} className="w-full inline-flex justify-center bg-lime-500 hover:bg-lime-600 disabled:opacity-60 text-emerald-950 font-black p-4 rounded-xl transition shadow-lg shadow-lime-500/10 text-base">
              {formStatus.sending ? 'Sending...' : 'Send Enquiry'}
            </button>
            {formStatus.message && <p className={`text-sm font-bold ${formStatus.error ? 'text-red-600' : 'text-emerald-700'}`}>{formStatus.message}</p>}
          </motion.form>
        </div>
      </motion.section>


      {/* 9. GOOGLE MAP */}
      <motion.section initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={heroVariants.container} className="max-w-7xl mx-auto px-4 py-24">
        <div className="text-center max-w-xl mx-auto mb-16">
          <span className="text-lime-600 font-extrabold uppercase tracking-wider text-xs block">Our Location</span>
          <h2 className="text-3xl md:text-5xl font-black text-emerald-950 tracking-tight mt-1">Find Us on Google Maps</h2>
          <a href={businessMapUrl} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center justify-center gap-2 text-sm font-extrabold text-emerald-800 hover:text-lime-600">
            <MapPin size={17} />
            {businessAddress}
          </a>
          <div className="h-1.5 w-16 bg-lime-500 mx-auto rounded-full mt-3"></div>
        </div>
        <motion.div variants={heroVariants.fadeUp} className="relative rounded-[2.5rem] overflow-hidden shadow-2xl border border-slate-200 bg-slate-100">
          <iframe
            src={businessMapEmbedUrl}
            title="Landscaping Christchurch Google Map"
            className="h-[520px] w-full"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </motion.div>
      </motion.section>

    </div>
  );
}
